import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import { logger } from '../../utils/logger.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/text-button.js'
import '@material/web/checkbox/checkbox.js'
import '@material/web/icon/icon.js'
import '@material/web/progress/circular-progress.js'

const log = logger.create('eula')

@customElement('eula-acceptance')
export class EulaAcceptance extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: var(--cairn-spacing-xl, 32px) var(--cairn-spacing-md, 16px);
      box-sizing: border-box;
    }

    .container {
      max-width: 400px;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    h2 {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-xs, 4px);
    }

    .subtitle {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 var(--cairn-spacing-xl, 32px);
      text-align: center;
      line-height: var(--cairn-line-height, 1.5);
    }

    .terms-summary {
      width: 100%;
      max-height: 320px;
      overflow-y: auto;
      padding: var(--cairn-spacing-md, 16px);
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius-lg, 12px);
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      line-height: var(--cairn-line-height, 1.5);
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .terms-summary h3 {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: var(--cairn-font-weight-medium, 500);
      color: var(--cairn-text-color, #1e293b);
      margin: var(--cairn-spacing-md, 16px) 0 var(--cairn-spacing-xs, 4px);
    }

    .terms-summary h3:first-child {
      margin-top: 0;
    }

    .terms-summary p {
      margin: 0 0 var(--cairn-spacing-sm, 8px);
    }

    .terms-summary ul {
      margin: 0 0 var(--cairn-spacing-sm, 8px);
      padding-left: var(--cairn-spacing-lg, 24px);
    }

    .terms-summary li {
      margin-bottom: var(--cairn-spacing-xs, 4px);
    }

    .links {
      display: flex;
      gap: var(--cairn-spacing-md, 16px);
      margin-bottom: var(--cairn-spacing-lg, 24px);
      width: 100%;
      justify-content: center;
    }

    .links a {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-primary-color, #3b82f6);
      text-decoration: none;
    }

    .links a:hover {
      text-decoration: underline;
    }

    .agreement {
      display: flex;
      align-items: flex-start;
      gap: var(--cairn-spacing-sm, 8px);
      width: 100%;
      padding: var(--cairn-spacing-md, 16px);
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius-lg, 12px);
      cursor: pointer;
    }

    .agreement md-checkbox {
      margin-top: 2px;
    }

    .agreement-text {
      flex: 1;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-color, #1e293b);
      line-height: var(--cairn-line-height, 1.5);
    }

    .actions {
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
      width: 100%;
      margin-top: var(--cairn-spacing-lg, 24px);
    }

    md-filled-button {
      width: 100%;
      --md-filled-button-container-height: 48px;
      --md-filled-button-container-shape: var(--cairn-border-radius, 8px);
    }

    .error-message {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-error-color, #dc2626);
      margin: 0;
      text-align: center;
    }

    .loading-overlay {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      padding: var(--cairn-spacing-xl, 32px);
      color: var(--cairn-text-secondary-color, #64748b);
    }
  `

  @state() private agreed = false
  @state() private saving = false
  @state() private error = ''

  private toggleAgreed(): void {
    this.agreed = !this.agreed
  }

  private async handleAccept(): Promise<void> {
    if (!this.agreed) return
    this.saving = true
    this.error = ''

    try {
      await authService.acceptEula()
      const { router } = await import('../../utils/router.js')
      router.navigate('/')
    } catch (err) {
      log.error('EULA acceptance failed:', err)
      this.error = 'Failed to save. Please try again.'
      this.saving = false
    }
  }

  override render(): unknown {
    if (this.saving) {
      return html`
        <div class="container">
          <div class="loading-overlay">
            <md-circular-progress indeterminate></md-circular-progress>
            <span>Saving...</span>
          </div>
        </div>
      `
    }

    return html`
      <div class="container">
        <h2>Terms of Service</h2>
        <p class="subtitle">Please review and accept our terms to continue</p>

        <div class="terms-summary">
          <h3>What you're agreeing to</h3>
          <p>
            By using Taselith, you agree to our End User License Agreement and Privacy Policy. Here
            are the key points:
          </p>

          <h3>Your Content</h3>
          <p>
            Routes, plans, and other content you create remain yours. You grant us a license to host
            and display it for app functionality and team collaboration.
          </p>

          <h3>Community Standards</h3>
          <ul>
            <li>Do not upload harmful, abusive, or misleading content</li>
            <li>Do not share false information about trail conditions or hazards</li>
            <li>Respect other users and their privacy</li>
          </ul>
          <p>
            We may remove content that violates these standards and suspend accounts of repeat
            offenders.
          </p>

          <h3>Outdoor Safety</h3>
          <p>
            Backcountry activities are inherently dangerous. Taselith is a planning and navigation
            aid, not a substitute for proper preparation, skills, and judgment. Map data, weather
            forecasts, and trail information may be inaccurate or outdated. Always carry backup
            navigation and verify conditions from official sources.
          </p>

          <h3>Your Privacy</h3>
          <p>
            We collect location data, activity tracks, and account information to provide our
            services. We do not sell your data or use third-party advertising. See our Privacy
            Policy for full details.
          </p>
        </div>

        <div class="links">
          <a href="/eula.html" target="_blank">Full Terms of Service</a>
          <a href="/privacy.html" target="_blank">Privacy Policy</a>
        </div>

        <div class="agreement" @click=${this.toggleAgreed}>
          <md-checkbox .checked=${this.agreed}></md-checkbox>
          <span class="agreement-text">
            I have read and agree to the Terms of Service and Privacy Policy
          </span>
        </div>

        ${this.error ? html`<p class="error-message">${this.error}</p>` : nothing}

        <div class="actions">
          <md-filled-button ?disabled=${!this.agreed} @click=${this.handleAccept}
            >Accept and Continue</md-filled-button
          >
        </div>
      </div>
    `
  }
}
